/**
 * Typed client for the WaveQA Admin Portal API.
 *
 * **A separate module from `api.ts`, with its own access token.** The two
 * clients deliberately share nothing: an administrator and a customer can be
 * signed in in the same browser, and a single module-level token would let the
 * later sign-in overwrite the earlier one — sending an admin bearer to customer
 * endpoints, or the reverse. The API refuses both (the tokens carry different
 * `typ` claims), so the failure would be a confusing 403 rather than a breach,
 * but it would be a failure with no reason a user could see.
 *
 * The token is a module variable rather than `localStorage`, for the reason the
 * customer client gives: a token in storage is readable by any script on the
 * page, so one XSS bug hands over a working session. Here it dies with the tab,
 * and the long-lived refresh token stays in an HttpOnly cookie scoped to
 * `/api/v1/admin/auth`, which script cannot touch at all.
 */

import { ApiError } from './api';

// The same build-time base as the customer client, plus the admin prefix.
//
// In development `VITE_API_BASE_URL` is unset, so this stays a same-origin
// `/api/v1/admin` and Vite's proxy forwards it to the local API. In production
// it resolves to `https://api.waveqa-agent.com/api/v1/admin`.
//
// This was previously a hardcoded relative path, which worked in development
// for exactly that reason and then pointed every admin call at the *web* origin
// once deployed. Nothing there serves the API: the request either 404s or, with
// the SPA fallback in place, comes back as the HTML shell with `200 OK` - so
// sign-in failed by way of a JSON parse error rather than anything a reader
// could act on.
const ADMIN_BASE = `${import.meta.env.VITE_API_BASE_URL ?? '/api/v1'}/admin`;

let adminAccessToken: string | null = null;

export function setAdminAccessToken(token: string | null): void {
  adminAccessToken = token;
}

export function getAdminAccessToken(): string | null {
  return adminAccessToken;
}

interface ProblemDetails {
  title?: string;
  detail?: string;
  code?: string;
  fields?: Record<string, string[]>;
}

interface RequestOptions extends RequestInit {
  /** Set for calls that must not attempt a token refresh on 401. */
  noRetry?: boolean;
}

async function request<T>(path: string, init: RequestOptions = {}): Promise<T> {
  const { noRetry, ...rest } = init;

  const response = await fetch(`${ADMIN_BASE}${path}`, {
    ...rest,
    // `include` rather than `same-origin`: the API is on its own origin in
    // production, and `same-origin` omits the refresh cookie on a cross-origin
    // request. That would sign an administrator out the moment their
    // fifteen-minute access token expired, with the refresh silently failing.
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(rest.body ? { 'Content-Type': 'application/json' } : {}),
      ...(adminAccessToken ? { Authorization: `Bearer ${adminAccessToken}` } : {}),
      ...rest.headers,
    },
  });

  // One silent refresh. Access tokens are fifteen minutes by design, so meeting
  // an expired one mid-session is expected rather than exceptional.
  if (response.status === 401 && !noRetry && adminAccessToken) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      return request<T>(path, { ...init, noRetry: true });
    }
  }

  if (!response.ok) {
    // The API answers failures as ProblemDetails, but a proxy fault can return
    // HTML. Parsing defensively keeps a 502 from surfacing as a JSON error.
    let problem: ProblemDetails = {};
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      /* non-JSON error body */
    }

    throw new ApiError(
      problem.detail ?? problem.title ?? `Request failed (${response.status})`,
      response.status,
      problem.code,
      problem.fields,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * The in-flight refresh, shared by every caller.
 *
 * Refresh tokens rotate, and the server treats a second presentation of an
 * already-rotated token as replay — revoking the whole family, correctly, since
 * that is what a stolen token looks like. So the client must never present one
 * twice concurrently: two tabs reloading together, or a retry racing the initial
 * restore, would otherwise sign the administrator out entirely.
 */
let refreshInFlight: Promise<AdminSession> | null = null;

export async function refreshAdminSession(): Promise<AdminSession> {
  refreshInFlight ??= request<AdminSession>('/auth/refresh', {
    method: 'POST',
    noRetry: true,
  }).finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

async function tryRefresh(): Promise<boolean> {
  try {
    const session = await refreshAdminSession();
    setAdminAccessToken(session.access_token);
    return true;
  } catch {
    setAdminAccessToken(null);
    return false;
  }
}

// --- Contracts ------------------------------------------------------------

export type PlanName = 'free' | 'basic' | 'standard' | 'premium';
export type Granularity = 'monthly' | 'quarterly' | 'yearly' | 'custom';

export interface AdminProfile {
  id: string;
  email: string;
  display_name: string | null;
  status: string;
  mfa_enabled: boolean;
  last_login_at: string | null;
  password_changed_at: string | null;
  created_at: string;
}

export interface AdminSession {
  access_token: string;
  token_type: 'Bearer';
  expires_at: string;
  refresh_token: null;
  admin: AdminProfile;
}

export interface AdminChallenge {
  challenge_id: string;
  expires_at: string;
  otp_digits: number;
  message: string;
}

/** Exactly one half is set — see `AdminLoginResponse` on the API. */
export interface AdminLoginResult {
  challenge: AdminChallenge | null;
  session: AdminSession | null;
}

export interface TrendTile {
  value: number;
  previous: number;
  /** Null when there is no honest comparison: an empty window, or a snapshot. */
  change_percent: number | null;
}

export interface MoneyTile {
  minor_units: number;
  previous_minor_units: number;
  change_percent: number | null;
  currency: string;
}

export interface PlanRow {
  plan: PlanName;
  display_name: string;
  /** Active plus trialing. */
  subscribers: number;
  /** Active only. This is the column that sums to MRR. */
  billing_subscribers: number;
  monthly_price_minor_units: number | null;
  monthly_revenue_minor_units: number;
}

export interface SubscriptionStates {
  active: number;
  trialing: number;
  past_due: number;
  paused: number;
  canceled: number;
  pending: number;
  /** Derived: the period ended, so the agent is already being refused. */
  expired: number;
}

export interface SeriesPoint {
  label: string;
  period_start: string;
  value: number;
}

export interface Dashboard {
  total_users: TrendTile;
  active_users: TrendTile;
  paid_users: TrendTile;
  mrr: MoneyTile;
  revenue_this_month: MoneyTile;
  revenue_this_year: MoneyTile;
  plans: PlanRow[];
  users_without_subscription: number;
  subscriptions: SubscriptionStates;
  revenue_series: SeriesPoint[];
  user_growth_series: SeriesPoint[];
  qa_runs_total: number;
  year: number;
  currency: string;
  has_excluded_currency_payments: boolean;
}

export interface RevenueStats {
  total_minor_units: number;
  this_month_minor_units: number;
  last_month_minor_units: number;
  this_year_minor_units: number;
  average_month_minor_units: number;
  mrr_minor_units: number;
  arr_minor_units: number;
  currency: string;
}

export interface Revenue {
  series: SeriesPoint[];
  stats: RevenueStats;
  plans: PlanRow[];
  granularity: Granularity;
  year: number;
  plan: PlanName | null;
  currency: string;
  excluded_payments: number;
  /** A plan filter resolves through the customer's *current* subscription. */
  plan_filter_is_approximate: boolean;
}

export interface AdminUserRow {
  id: string;
  email: string;
  display_name: string | null;
  status: string;
  created_at: string;
  last_login_at: string | null;
  plan: string | null;
  subscription_status: string | null;
  revenue_minor_units: number;
  /** End of the launch-promotion window, past or future. Null if never enrolled. */
  promotion_ends_at: string | null;
  promotion_active: boolean;
  /** Paid plan, no open window, nothing paying for it. */
  awaiting_payment: boolean;
}

export interface UserPage {
  rows: AdminUserRow[];
  total: number;
  page: number;
  page_size: number;
  currency: string;
}

export interface AuditRow {
  id: string;
  event_type: string;
  occurred_at: string;
  succeeded: boolean;
  reason: string | null;
  ip_address: string | null;
  user_id: string | null;
  actor_admin_id: string | null;
  actor_admin_email: string | null;
  metadata: Record<string, string> | null;
}

export interface SubscriptionDetail {
  plan: string;
  status: string;
  agent_limit: number;
  monthly_hour_allowance: number;
  hours_used_this_period: number;
  criteria_used_this_period: number;
  monthly_criteria_allowance: number | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at: string | null;
  canceled_at: string | null;
  is_entitled: boolean;
  promotion_ends_at: string | null;
  promotion_started_at: string | null;
  promotion_active: boolean;
  /** Windows this account has had: one at activation, one per operator grant. */
  promotion_grants: number;
  requires_payment: boolean;
}

export interface UserDetail {
  user: AdminUserRow;
  subscription: SubscriptionDetail | null;
  credentials_active: number;
  credentials_total: number;
  qa_runs: number;
  email_verified_at: string | null;
  time_zone: string;
  recent_events: AuditRow[];
  currency: string;
}

export interface PlanFeatures {
  llm_configuration: boolean;
  security_baseline_harness: boolean;
  security_compliance_qa: boolean;
  waveqa_self_adaptation: boolean;
  limit_llm_calls: boolean;
  separate_qa_reports: boolean;
  customer_support: boolean;
}

export interface PricingPlan {
  plan: PlanName;
  display_name: string;
  monthly_price_minor_units: number | null;
  currency: string;
  agent_limit: number;
  monthly_hours: number;
  unlimited_hours: boolean;
  monthly_criteria: number | null;
  max_projects: number | null;
  localhost_only: boolean;
  harnesses: string[];
  features: PlanFeatures;
  subscribers: number;
  billing_subscribers: number;
  monthly_revenue_minor_units: number;
  purchasable: boolean;
}

export interface PricingPlans {
  plans: PricingPlan[];
  currency: string;
  editable: boolean;
  editing_note: string;
}

export interface AdminRow {
  id: string;
  email: string;
  display_name: string | null;
  status: string;
  mfa_enabled: boolean;
  last_login_at: string | null;
  created_at: string;
  recently_active: boolean;
  /** True for the caller, so the UI can disable what the API would 409 on. */
  is_self: boolean;
  /**
   * Whether this address is on the server's admin sign-in allowlist.
   *
   * False means the row exists but can never be used. Shown as such rather than
   * left to look like working access.
   */
  can_sign_in: boolean;
}

export interface AuditPage {
  rows: AuditRow[];
  total: number;
  page: number;
  page_size: number;
}

export interface UserFilters {
  search?: string;
  plan?: string;
  status?: string;
  promotion?: PromotionState;
  page?: number;
  pageSize?: number;
}

/**
 * The launch-promotion states the directory can be filtered by.
 *
 * `awaiting_payment` is the one an operator actually reaches for: a paid plan
 * whose window has closed with nothing paying for it. Assembling that selection
 * by reading dates off a hundred rows is how it gets got wrong.
 */
export type PromotionState =
  | 'active'
  | 'ending_soon'
  | 'lapsed'
  | 'none'
  | 'awaiting_payment';

export interface PromotionSettings {
  enabled: boolean;
  duration_months: number;
  updated_at: string | null;
  updated_by_admin_id: string | null;
  /** Bounds the form enforces, served so the field and its validation agree. */
  min_months: number;
  max_months: number;
  /** False until a payment gateway is wired up. */
  payment_gateway_available: boolean;
  payment_gateway_provider: string | null;
}

export interface PromotionStats {
  active: number;
  ending_soon: number;
  lapsed: number;
  never_enrolled: number;
  awaiting_payment: number;
  ending_soon_days: number;
}

export interface PromotionOverview {
  settings: PromotionSettings;
  stats: PromotionStats;
}

export interface GrantOutcome {
  user_id: string;
  email: string;
  promotion_ends_at: string;
  /** True when a live window was lengthened, false when a closed one restarted. */
  extended: boolean;
  /** Whether this put a plan suspended for payment back into service. */
  reinstated: boolean;
}

export interface GrantResult {
  granted: GrantOutcome[];
  stats: PromotionStats;
}

export interface RevokeResult {
  /** Only the accounts that had an open window. The rest were not touched. */
  revoked: string[];
  stats: PromotionStats;
}

/** Drops empty filters so the URL carries only what the operator actually set. */
function query(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : '';
}

export const adminApi = {
  // --- Authentication -----------------------------------------------------

  login: (email: string, password: string) =>
    request<AdminLoginResult>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  verifyOtp: (challenge_id: string, code: string) =>
    request<AdminSession>('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ challenge_id, code }),
    }),

  resendOtp: (challenge_id: string) =>
    request<AdminChallenge>('/auth/resend-otp', {
      method: 'POST',
      body: JSON.stringify({ challenge_id }),
    }),

  /** Always goes through the shared in-flight promise — see `refreshAdminSession`. */
  refresh: refreshAdminSession,

  logout: () => request<{ message: string }>('/auth/logout', { method: 'POST' }),

  me: () => request<AdminProfile>('/auth/me'),

  changePassword: (body: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }) =>
    request<{ message: string }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // --- Portal -------------------------------------------------------------

  getDashboard: (year?: number) => request<Dashboard>(`/dashboard${query({ year })}`),

  getRevenue: (params: {
    granularity?: Granularity;
    year?: number;
    plan?: string;
    start?: string;
    end?: string;
  }) => request<Revenue>(`/revenue${query(params)}`),

  getUsers: (filters: UserFilters) =>
    request<UserPage>(
      `/users${query({
        search: filters.search,
        plan: filters.plan,
        status: filters.status,
        promotion: filters.promotion,
        page: filters.page,
        page_size: filters.pageSize,
      })}`,
    ),

  getUser: (id: string) => request<UserDetail>(`/users/${id}`),

  setUserStatus: (id: string, status: 'suspended' | 'active', reason: string | null) =>
    request<UserDetail>(`/users/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status, reason }),
    }),

  getPlans: () => request<PricingPlans>('/plans'),

  // --- Launch promotion ---------------------------------------------------

  getPromotions: () => request<PromotionOverview>('/promotions'),

  /**
   * Switch the programme, or change the window new accounts are given.
   *
   * Both fields are optional and omitted means unchanged — the screen can
   * toggle the programme without restating the length, and the API must not
   * read a missing field as `false`.
   */
  updatePromotionSettings: (body: { enabled?: boolean; duration_months?: number }) =>
    request<PromotionOverview>('/promotions/settings', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  grantPromotion: (body: { user_ids: string[]; months: number; reason: string | null }) =>
    request<GrantResult>('/promotions/grant', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  revokePromotion: (body: { user_ids: string[]; reason: string | null }) =>
    request<RevokeResult>('/promotions/revoke', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getAdmins: () => request<AdminRow[]>('/admins'),

  createAdmin: (body: { email: string; password: string; display_name: string | null }) =>
    request<AdminRow>('/admins', { method: 'POST', body: JSON.stringify(body) }),

  setAdminStatus: (id: string, status: 'active' | 'disabled') =>
    request<AdminRow>(`/admins/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),

  setAdminMfa: (id: string, enabled: boolean) =>
    request<AdminRow>(`/admins/${id}/mfa`, {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    }),

  getAudit: (page = 1, pageSize = 50) =>
    request<AuditPage>(`/audit${query({ page, page_size: pageSize })}`),
};

// --- Formatting -----------------------------------------------------------

/**
 * Minor units as currency.
 *
 * Compact by default: a revenue axis with `$418,200` at every tick is mostly
 * punctuation. `exact` is for the figures a reader will reconcile against
 * something else, where rounding to `$418K` would make that impossible.
 */
export function formatMoney(
  minorUnits: number,
  currency = 'USD',
  options: { exact?: boolean } = {},
): string {
  const major = minorUnits / 100;

  if (!options.exact && Math.abs(major) >= 10_000) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(major);
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(major);
}

export function formatCount(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

export function formatDate(value: string | null): string {
  if (!value) return '—';
  // The API sends naive UTC. Appending Z stops the browser reading it as local
  // time, which would shift every timestamp by the viewer's offset.
  const iso = value.endsWith('Z') || value.includes('+') ? value : `${value}Z`;
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const iso = value.endsWith('Z') || value.includes('+') ? value : `${value}Z`;
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
